import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export const inputCls =
  "rounded-sm border-ink/15 bg-white focus-visible:ring-1 focus-visible:ring-gold focus-visible:border-gold h-11 text-base sm:text-sm";

export const selectCls =
  "rounded-sm border border-ink/15 bg-white focus:ring-1 focus:ring-gold focus:border-gold h-11 text-base sm:text-sm w-full px-3 outline-none transition-colors duration-200";

export function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-ink/70 mb-1.5">
        {label} {required && <span className="text-gold">*</span>}
      </label>
      {children}
    </div>
  );
}

export function Honeypot({ value, onChange }) {
  return (
    <input
      type="text"
      name="website"
      value={value}
      onChange={onChange}
      tabIndex={-1}
      autoComplete="off"
      className="hidden"
      aria-hidden="true"
    />
  );
}

export function useSubmit(endpoint, successMessage) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (payload) => {
    setLoading(true);
    try {
      await api.post(endpoint, payload);
      setSuccess(true);
      toast.success(successMessage);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { submit, loading, success, setSuccess };
}

export function SubmitButton({ loading, children, testId, className = "" }) {
  return (
    <button
      type="submit"
      data-testid={testId}
      disabled={loading}
      className={`btn-sweep w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold text-sm tracking-wide px-7 py-4 rounded-sm hover:bg-darknavy transition-colors duration-300 disabled:opacity-60 ${className}`}
    >
      {loading && <Loader2 size={16} className="animate-spin" />}
      {loading ? "Submitting..." : children}
    </button>
  );
}

export function SuccessBlock({ message, onReset, testId, contactEmail = "info@maqlinesolutions.com" }) {
  return (
    <div className="border border-gold/40 bg-gold/5 rounded-sm p-8 text-center" data-testid={testId}>
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gold/15 flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-gold" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <p className="text-ink font-semibold text-lg mb-2">{message}</p>
      <p className="text-muted text-sm mb-5">You can also reach us directly at <a href={`mailto:${contactEmail}`} className="text-navy font-semibold hover:text-gold transition-colors">{contactEmail}</a>.</p>
      {onReset && (
        <button onClick={onReset} className="text-sm font-semibold text-navy hover:text-gold transition-colors duration-200 underline underline-offset-4">
          Submit another response
        </button>
      )}
    </div>
  );
}

export const validPhone = (p) => /^[0-9+\-\s]{10,15}$/.test(p || "");
export const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || "");
